import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { enterpriseLicenseGrantsAccess } from '@/lib/orgContract';

export const ENTERPRISE_PLAN_ID = '786a666b-1b3a-40c5-8ee9-54de870d52c6';


/**
 * Rutas de administrador habilitadas en el plan gratuito empresarial.
 * Todo lo demás se muestra en el menú pero bloqueado, con invitación a mejorar de plan.
 */
export const FREE_PLAN_ALLOWED_PATHS = [
  '/dashboard',
  '/enterprise-diagnostic',
  '/user-management',
  '/cohort-management',
  '/enterprise-dashboard',
  '/ai-portfolio',
  '/support',
  '/settings',
  '/profile',
] as const;

export interface OrgPlan {
  planTier: 'free' | 'enterprise';
  isFreePlan: boolean;
  organizationId: string | null;
  organizationName: string | null;
  emailDomain: string | null;
  isLoading: boolean;
  /** true si la ruta está habilitada con el plan actual */
  canAccess: (path: string) => boolean;
}

export function useOrgPlan(): OrgPlan {
  const { user, profile, currentRole } = useAuth();
  const organizationId = profile?.organization_id ?? null;

  const { data, isLoading } = useQuery({
    queryKey: ['org-plan', organizationId, user?.id],
    queryFn: async () => {
      const [orgRes, subRes] = await Promise.all([
        organizationId
          ? supabase
              .from('organizations')
              .select('id, name, plan_tier, email_domain, contract_start_date, contract_end_date')
              .eq('id', organizationId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        supabase
          .from('user_subscriptions')
          .select('id')
          .eq('user_id', user!.id)
          .eq('plan_id', ENTERPRISE_PLAN_ID)
          .eq('status', 'active')
          .limit(1),
      ]);

      const org = (orgRes as any)?.data ?? null;
      const hasEnterpriseLicense = ((subRes as any)?.data ?? []).length > 0;
      // La vigencia del contrato es de la organización: fuera de ella, todos pasan a Gratuito.
      const grants = enterpriseLicenseGrantsAccess({
        hasLicense: hasEnterpriseLicense,
        planTier: org?.plan_tier,
        start: org?.contract_start_date,
        end: org?.contract_end_date,
      });

      return {
        organizationName: org?.name ?? null,
        emailDomain: org?.email_domain ?? null,
        planTier: grants ? ('enterprise' as const) : ('free' as const),
      };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Super admin nunca se ve afectado por el plan de una organización.
  const planTier =
    currentRole === 'super_admin' ? 'enterprise' : data?.planTier ?? 'enterprise';
  const isFreePlan = planTier === 'free';

  return {
    planTier,
    isFreePlan,
    organizationId,
    organizationName: data?.organizationName ?? null,
    emailDomain: data?.emailDomain ?? null,
    isLoading,
    canAccess: (path: string) =>
      !isFreePlan || FREE_PLAN_ALLOWED_PATHS.some((p) => path === p || path.startsWith(`${p}/`)),
  };
}
